import * as React from "react";
import { Link } from "react-router-dom";
import { useGames } from "../hooks/useGames.js";
import { useMe } from "../hooks/useMe.js";
import { usePlacementDone } from "../hooks/usePlacementDone.js";
import { useStartDiagnostic } from "../hooks/useStartDiagnostic.js";
import { useSubscribe } from "../hooks/useSubscribe.js";
import { useDocumentTitle } from "../hooks/useDocumentTitle.js";
import { SEO_TITLES } from "../lib/seo-titles.js";
import type { GameListItem, GamesListResponse } from "../lib/types.js";
import { FieldNav } from "../components/FieldNav.js";
import { FieldFooter } from "../components/FieldFooter.js";
import { PosterArt, artKindForSlug } from "../components/FieldArt.js";
import {
  GamesGallery,
  GRADES_LABEL,
  type GalleryVariant,
  type Poster,
} from "../components/GamesGallery.js";
import { TitleRolodex } from "../components/TitleRolodex.js";
import { MontageVideo } from "../components/MontageVideo.js";
import "../styles/landing-field.css";
import "../styles/placement-intro-field.css";

/**
 * "Field Day" landing page — bone background, IntegralCF display type, orange
 * + blue + yellow blocks. Brought over from the Claude Design handoff
 * (sports-academy-games / Landing Page.html, VField variant).
 *
 * Real game data is pulled from `useGames` and mapped to the design's poster
 * format. Static fallbacks are used while loading or on error so the layout
 * never flashes empty.
 *
 * Layout (top → bottom): hero w/ title rolodex, ticker, stats,
 * games grid, dark Quest Path (placement-test funnel), dark How-it-works,
 * orange Closer w/ mini-funnel + trust line, FieldFooter.
 */
export function LandingRoute({
  galleryVariant,
}: { galleryVariant?: GalleryVariant } = {}): React.ReactElement {
  // Seed the catalog query from the last list we saw (persisted in
  // localStorage). The /v1/games response is environment-specific (dev and prod
  // ship different live sets), so a static seed can never match every env — but
  // the *previous* response always does. Reading it on the first render means
  // every load after the very first paints the real catalog instantly (no
  // skeleton, no count/order reflow), while a background revalidate keeps it
  // fresh. The static SEED_POSTERS below only covers the true cold start.
  // The A/B gallery variants (/a-/e) all self-canonicalize to "/", so they
  // share its title too.
  useDocumentTitle(SEO_TITLES["/"]);
  const initialGames = React.useMemo(() => readCachedGames(), []);
  const { data, loading, err } = useGames(
    { status: "active", limit: 100 },
    { initialData: initialGames },
  );
  React.useEffect(() => {
    if (data) writeCachedGames(data);
  }, [data]);
  const { start: startDiagnostic, busy: startingDiagnostic } = useStartDiagnostic();
  // Funnel CTA: a fresh/anonymous visitor is nudged to take the placement; once
  // they've placed we stop re-prompting the test (the reported confusion) and
  // pivot the same buttons to "Subscribe Now" (free) or straight into the
  // arcade (premium). `placementDone` is false until the signed-in existence
  // check resolves, so the placement copy is the safe default during load.
  const { data: me } = useMe();
  const { done: placementDone, inProgress: placementInProgress } = usePlacementDone();
  // Placed-free users subscribe from here to start playing — send them into the
  // arcade after checkout (rides the Stripe success URL as ?next=), not the
  // default /dashboard.
  const { subscribe, busy: subscribing, emailModal } = useSubscribe({ next: "/games" });
  const funnel: FunnelKind =
    me?.user && placementDone
      ? me.premium
        ? "play"
        : "subscribe"
      : placementInProgress
        ? "resume"
        : "placement";
  const totalGames = data?.games.length ?? 0;
  const posters = React.useMemo<Poster[]>(() => {
    const live =
      data && data.games.length > 0 ? orderGames(data.games).map(toPoster) : SEED_POSTERS;
    return [...live, ...UPCOMING_POSTERS];
  }, [data]);

  return (
    <div className="v-field">
      <FieldNav />

      <main id="main">
        {/* HERO */}
        <header className="v-field__hero">
          <div className="container">
            <div className="v-field__hero-grid">
              <div>
                <span className="v-field__hero-tag">K–12 Math · Made for kids</span>
                <h1 className="v-field__hero-title">
                  Math games that actually <span className="stripe">teach.</span>
                </h1>
                <p className="v-field__hero-sub">
                  How can you expect your kid to love math when they're doing boring worksheets all
                  day?
                  <br />
                  <br />
                  We find every single video game that kids love playing and turn it into a safe,
                  kid-friendly video game for them to drill their math concepts.
                </p>
                <div className="v-field__hero-ctas">
                  <FunnelCta
                    funnel={funnel}
                    className="btn-field-primary"
                    style={{ color: funnel === "play" ? undefined : "#fff" }}
                    labels={{
                      placement: "Start Placement Test - Free",
                      resume: "Resume placement",
                      subscribe: "Subscribe Now",
                      play: "Enter the arcade",
                    }}
                    startPlacement={() => void startDiagnostic()}
                    placementBusy={startingDiagnostic}
                    subscribe={() => subscribe()}
                    subscribeBusy={subscribing}
                  />
                  {/* Secondary free hook (TEX-2013): Quiz Studio, side-by-side with
                    the primary (wraps below on narrow screens). */}
                  <Link to="/quizzes" className="btn-field-secondary">
                    Or turn your notes into a game — Free
                  </Link>
                </div>
                {funnel === "placement" && (
                  <p className="v-field__hero-meta">
                    <span className="dot" aria-hidden="true" />
                    <span>
                      <strong>5 minutes</strong> · No signup · Adaptive · Reveals your rank
                    </span>
                  </p>
                )}
              </div>
              <TitleRolodex posters={posters} loading={loading} />
            </div>
          </div>
        </header>

        {/* SEE IT IN ACTION — gameplay montage band (TEX-2238) */}
        <section className="v-field__reel" aria-labelledby="reel-h">
          <div className="container">
            <div className="v-field__reel-head">
              <span className="v-field__sec-eyebrow">See it in action</span>
              <h2 id="reel-h" className="v-field__reel-title">
                Every level is real <span className="stripe">math.</span>
              </h2>
            </div>
            <MontageVideo className="v-field__reel-video" />
          </div>
        </section>

        {/* TICKER */}
        <div className="v-field__ticker" aria-hidden="true">
          <div className="v-field__ticker-track">
            {[...QUOTES, ...QUOTES].map((q, i) => (
              <span key={i} className="v-field__ticker-item">
                <span className="dot">★</span>
                <span>{q}</span>
              </span>
            ))}
          </div>
        </div>

        {/* WHY IT WORKS */}
        <section className="why-v5" aria-labelledby="stats-h">
          <div className="container" style={{ paddingBottom: 0 }}>
            <span className="v-field__sec-eyebrow">Why it works</span>
            <h2 id="stats-h" className="v-field__sec-title">
              What sets
              <br />
              us apart.
            </h2>
          </div>
          {WHY_ITEMS.map((item, i) => (
            <div key={i} className={`why-v5__band${i % 2 === 1 ? " why-v5__band--alt" : ""}`}>
              <div className="container">
                <div className={`why-v5__inner${i % 2 === 1 ? " why-v5__inner--flip" : ""}`}>
                  <span className="why-v5__num">0{i + 1}</span>
                  <div className="why-v5__text">
                    <h3 className="why-v5__title">{item.title}</h3>
                    <p className="why-v5__desc">{item.desc}</p>
                  </div>
                  <div className="why-v5__stats">
                    {item.stats.map((s) => (
                      <div key={s.label} className="why-v5__stat-box">
                        <span className="why-v5__stat-value">{s.value}</span>
                        <span className="why-v5__stat-label">{s.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* HOW IT WORKS */}
        <section className="v-field__how" id="how" aria-labelledby="how-h">
          <div className="container">
            <div className="v-field__how-head">
              <span className="v-field__sec-eyebrow">How it works</span>
              <h2 id="how-h" className="v-field__sec-title">
                Three steps
                <br />
                to <em>excel.</em>
              </h2>
              <p className="v-field__how-sub">
                From open-the-page to weekly mastery report in under ten minutes. No district
                install. No IT ticket.
              </p>
            </div>
            <div className="v-field__steps">
              <Step n="01" title="Take the placement test" icon={<ClipboardIcon size={28} />}>
                Twelve adaptive questions that play like a game. Five minutes, no signup — just
                answers.
              </Step>
              <Step n="02" title="Get a personalized curriculum" icon={<RouteIcon size={28} />}>
                We pinpoint every gap and build a custom plan — right games, right level, right
                pace.
              </Step>
              <Step n="03" title="See progress" icon={<ChartIcon size={28} />}>
                Accuracy, topics mastered, and time-on-task — sent to parents and teachers, weekly.
              </Step>
            </div>
          </div>
        </section>

        {/* GAMES */}
        <section className="v-field__games" id="games" aria-labelledby="games-h">
          <div className="container">
            <div className="v-field__games-head">
              <div>
                <span className="v-field__sec-eyebrow">The Arcade</span>
                <h2 id="games-h" className="v-field__games-title">
                  Pick a game.
                  <br />
                  Play <span className="stripe">real math.</span>
                </h2>
              </div>
              <Link className="v-field__games-link" to="/games">
                View all{" "}
                {totalGames + UPCOMING_POSTERS.length > 0
                  ? totalGames + UPCOMING_POSTERS.length
                  : ""}{" "}
                <ArrowIcon size={14} />
              </Link>
            </div>
            {err && (
              <p style={{ marginBottom: 32, color: "#44474f" }}>
                Couldn't load live game list — showing samples.
              </p>
            )}
            {galleryVariant ? (
              <GamesGallery variant={galleryVariant} posters={posters} loading={loading} />
            ) : (
              <div className="v-field__games-grid" aria-busy={loading ? "true" : "false"}>
                {posters.map((p, i) => (
                  <Link
                    className="v-field__poster"
                    key={p.slug}
                    to={p.chip === "Coming soon" ? "/games" : `/games/${p.slug}`}
                  >
                    <div className="v-field__poster-art">
                      <div className="v-field__poster-chip">{p.chip}</div>
                      <PosterArt
                        thumbnail={p.thumbnail}
                        kind={p.art}
                        alt={`${p.title} cover art`}
                        priority={i < 4}
                      />
                    </div>
                    <div className="v-field__poster-body">
                      <h3 className="v-field__poster-title">{p.title}</h3>
                      <p className="v-field__poster-desc">{p.desc}</p>
                      <div className="v-field__poster-meta">
                        <span>{GRADES_LABEL}</span>
                        <span className="v-field__poster-play">
                          Play <PlayIcon size={10} />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* WHAT YOU'LL SEE AT THE END */}
        <section className="v-field-pt__sec">
          <div className="container">
            <span className="v-field-pt__sec-eye">What you'll see at the end</span>
            <h2 className="v-field-pt__sec-title">
              A personalized
              <br />
              <em>report card.</em>
            </h2>
            <p className="v-field-pt__sec-lede">
              Not a score, not a percentage. A read on where you actually are, what's already solid,
              and where to start playing.
            </p>

            <div className="v-field-pt__results">
              <div className="v-field-pt__results-list">
                <div className="v-field-pt__result-item">
                  <div className="ico">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <circle cx="12" cy="12" r="9" />
                      <circle cx="12" cy="12" r="5" />
                      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                    </svg>
                  </div>
                  <div>
                    <h3>Topics worth practicing</h3>
                    <p>
                      The specific skills the test thinks you'd benefit from working on next —
                      picked from what tripped you up, not what's "average" for your grade.
                    </p>
                  </div>
                </div>
                <div className="v-field-pt__result-item">
                  <div className="ico">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </div>
                  <div>
                    <h3>What's already solid</h3>
                    <p>
                      Topics you clearly have down. We won't make you grind these — you'll see them
                      flagged so you know what to skip.
                    </p>
                  </div>
                </div>
                <div className="v-field-pt__result-item">
                  <div className="ico">
                    <svg
                      width="28"
                      height="28"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M3 20h18" />
                      <path d="M6 16V10" />
                      <path d="M11 16V6" />
                      <path d="M16 16v-4" />
                      <path d="M21 16V4" />
                    </svg>
                  </div>
                  <div>
                    <h3>A starting grade per area</h3>
                    <p>
                      Numbers, Fractions, Algebra, Geometry, and Statistics each get their own
                      recommendation — because most students aren't at the same level across all of
                      them.
                    </p>
                  </div>
                </div>
              </div>

              <div className="v-field-pt__report">
                <span className="v-field-pt__report-tag">Sample result</span>
                <div className="v-field-pt__report-eye">
                  <span>Placement report</span>
                  <span className="pill">Rookie → Pro</span>
                </div>
                <div className="v-field-pt__report-name">Avery, age 9</div>
                <p className="v-field-pt__report-sub">Suggested starting grades by area</p>
                <div className="v-field-pt__report-grid">
                  <div className="v-field-pt__report-row solid">
                    <span className="area">Numbers</span>
                    <span className="bar">
                      <span className="bar-fill" style={{ width: "88%" }} />
                    </span>
                    <span className="grade">Gr 5</span>
                  </div>
                  <div className="v-field-pt__report-row gap">
                    <span className="area">Fractions</span>
                    <span className="bar">
                      <span className="bar-fill" style={{ width: "54%" }} />
                    </span>
                    <span className="grade">Gr 3</span>
                  </div>
                  <div className="v-field-pt__report-row">
                    <span className="area">Algebra</span>
                    <span className="bar">
                      <span className="bar-fill" style={{ width: "38%" }} />
                    </span>
                    <span className="grade">Gr 4</span>
                  </div>
                  <div className="v-field-pt__report-row solid">
                    <span className="area">Geometry</span>
                    <span className="bar">
                      <span className="bar-fill" style={{ width: "82%" }} />
                    </span>
                    <span className="grade">Gr 5</span>
                  </div>
                  <div className="v-field-pt__report-row gap">
                    <span className="area">Statistics</span>
                    <span className="bar">
                      <span className="bar-fill" style={{ width: "46%" }} />
                    </span>
                    <span className="grade">Gr 3</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CLOSER */}
        <section className="v-field__closer" aria-labelledby="cta-h">
          <div className="container">
            <span className="v-field__closer-eye">Day one · Start free</span>
            <h2 id="cta-h" className="v-field__closer-title">
              Stop fighting
              <br />
              over <span className="stripe">homework.</span>
            </h2>
            <p className="v-field__closer-sub">
              Take the five-minute placement game. We'll match your kid to the right level, the
              right games, and the right pace — before you ever pay a cent.
            </p>

            <div className="v-field__closer-steps">
              {CLOSER_STEPS.map((s) => (
                <div key={s.n} className="v-field__closer-step">
                  <span className="num">
                    <span className="dot" aria-hidden="true" /> Step {s.n}
                  </span>
                  <div className="name">{s.name}</div>
                  <div className="desc">{s.desc}</div>
                </div>
              ))}
            </div>

            <div className="v-field__closer-cta-wrap">
              <FunnelCta
                funnel={funnel}
                className="btn-field-light"
                style={{ color: funnel === "play" ? undefined : "#0f1115" }}
                labels={{
                  placement: "Start your free placement",
                  resume: "Resume placement",
                  subscribe: "Subscribe Now",
                  play: "Back to the arcade",
                }}
                startPlacement={() => void startDiagnostic()}
                placementBusy={startingDiagnostic}
                subscribe={() => subscribe()}
                subscribeBusy={subscribing}
              />
              {funnel === "placement" && (
                <div className="v-field__closer-trust">
                  <span className="check" aria-hidden="true">
                    ✓
                  </span>{" "}
                  No signup
                  <span className="sep" aria-hidden="true">
                    ·
                  </span>
                  <span className="check" aria-hidden="true">
                    ✓
                  </span>{" "}
                  No card
                  <span className="sep" aria-hidden="true">
                    ·
                  </span>
                  <span className="check" aria-hidden="true">
                    ✓
                  </span>{" "}
                  Cancel anytime
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <FieldFooter />
      {/* Phone-only accounts with no email get a capture step mid-checkout when
          the Subscribe funnel fires from here; null otherwise. */}
      {emailModal}
    </div>
  );
}

/**
 * Funnel CTA — the one primary action on the marketing surface, which depends
 * on who's looking:
 *   - "placement" → take the test (anonymous visitors + signed-in users who
 *     haven't placed yet). Unchanged from the original landing CTA.
 *   - "subscribe" → start Stripe checkout (placed, free).
 *   - "play"      → straight into the arcade (placed, premium).
 * Per-surface copy + styling ride in via props so the hero (orange) and the
 * closer (light) buttons stay visually distinct while sharing the branching.
 */
type FunnelKind = "placement" | "resume" | "subscribe" | "play";

function FunnelCta({
  funnel,
  className,
  style,
  labels,
  startPlacement,
  placementBusy,
  subscribe,
  subscribeBusy,
}: {
  funnel: FunnelKind;
  className: string;
  style?: React.CSSProperties;
  labels: { placement: string; resume: string; subscribe: string; play: string };
  startPlacement: () => void;
  placementBusy: boolean;
  subscribe: () => void;
  subscribeBusy: boolean;
}): React.ReactElement {
  if (funnel === "play") {
    return (
      <Link to="/games" className={className} style={style}>
        {labels.play}
        <span className="arrow-bubble">
          <ArrowIcon size={16} />
        </span>
      </Link>
    );
  }
  // Mid-placement: resume the in-flight run (DiagnosticPlayRoute → /active)
  // instead of starting a fresh one that would orphan it.
  if (funnel === "resume") {
    return (
      <Link to="/diagnostic/play" className={className} style={style}>
        {labels.resume}
        <span className="arrow-bubble">
          <ArrowIcon size={16} />
        </span>
      </Link>
    );
  }
  const isSubscribe = funnel === "subscribe";
  const busy = isSubscribe ? subscribeBusy : placementBusy;
  return (
    <button
      type="button"
      className={className}
      style={style}
      onClick={() => (isSubscribe ? subscribe() : startPlacement())}
      disabled={busy}
    >
      {busy
        ? isSubscribe
          ? "Redirecting…"
          : "Starting…"
        : isSubscribe
          ? labels.subscribe
          : labels.placement}
      <span className="arrow-bubble">
        <ArrowIcon size={16} />
      </span>
    </button>
  );
}

/**
 * Simple footer used by the Privacy / Terms / About routes. The landing page
 * has its own design-specific footer (FieldFooter) inline above; this one is
 * the plain version that fits the rest of the SPA's typography.
 */
export function Footer(): React.ReactElement {
  return (
    <footer
      className="container"
      style={{
        padding: "2rem var(--page-gutter)",
        color: "var(--color-fg-tertiary)",
        borderTop: "1px solid var(--color-border)",
        marginTop: "3rem",
        fontSize: "0.875rem",
      }}
    >
      <p style={{ margin: "0 0 0.5rem" }}>© Sports Academy Games</p>
      <p style={{ margin: 0 }}>
        <Link to="/about">About</Link>
        {" · "}
        <Link to="/privacy">Privacy</Link>
        {" · "}
        <Link to="/terms">Terms</Link>
      </p>
    </footer>
  );
}

// ─── how-step row ───────────────────────────────────────────────────────────
function Step({
  n,
  title,
  icon,
  children,
}: {
  n: string;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="v-field__step">
      <div className="v-field__step-num">{n}</div>
      <div className="v-field__step-body">
        <h3 className="v-field__step-title">{title}</h3>
        <p className="v-field__step-desc">{children}</p>
      </div>
      <div className="v-field__step-icon" aria-hidden="true">
        {icon}
      </div>
    </div>
  );
}

// ─── live → poster mapping ──────────────────────────────────────────────────

// Curated catalog order. `/v1/games` has no ORDER BY, so Postgres returns rows
// in an unspecified (effectively random) order — which is why the hero ring's
// front card used to change identity once the API resolved. The client owns the
// order instead: this drives BOTH the live ring and the loading-state seed
// below, so the two line up slot-for-slot and the ring never reshuffles.
//
// First entries are the games live in every environment (prod ⊆ dev); the tail
// is dev-only / not-yet-promoted, ordered last so promoting one slots it in
// after the always-live set without shifting it.
const GAME_ORDER = [
  "blaster-gate",
  "math-crush",
  "math-slice-dojo",
  "wizard-duel",
  "boxing",
  "math-strikers",
  "fast-math-defense",
  "smash-math-badminton",
  "sports-raiders",
];

const ORDER_INDEX = new Map(GAME_ORDER.map((slug, i) => [slug, i] as const));

/**
 * Deterministic catalog order. Listed slugs sort by their rank in GAME_ORDER;
 * any unlisted (newly shipped) slug sorts after them, alphabetically by name,
 * so it still renders predictably until it's added to GAME_ORDER.
 */
function orderGames(list: GameListItem[]): GameListItem[] {
  return [...list].sort((a, b) => {
    const ra = ORDER_INDEX.get(a.slug) ?? Number.MAX_SAFE_INTEGER;
    const rb = ORDER_INDEX.get(b.slug) ?? Number.MAX_SAFE_INTEGER;
    return ra - rb || a.name.localeCompare(b.name);
  });
}

// localStorage snapshot of the last catalog response, used to seed the games
// query so returning loads paint the real (environment-correct) catalog on the
// first render instead of the static seed. Versioned so a shape change can't
// deserialize a stale payload.
const GAMES_CACHE_KEY = "sa:landing:games:v1";

function readCachedGames(): GamesListResponse | undefined {
  if (typeof window === "undefined") return undefined;
  const raw = window.localStorage.getItem(GAMES_CACHE_KEY);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw) as GamesListResponse;
    if (parsed && Array.isArray(parsed.games)) return parsed;
    return undefined;
  } catch {
    // Corrupt entry — treat as a cache miss; the live fetch will repopulate it.
    return undefined;
  }
}

function writeCachedGames(data: GamesListResponse): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(GAMES_CACHE_KEY, JSON.stringify(data));
  } catch {
    // Storage unavailable (private mode / quota) — non-fatal; we just lose the
    // returning-visit seed and fall back to the static one.
  }
}

function toPoster(g: GameListItem, i: number): Poster {
  const chip =
    g.tags.find((t) => /top|new|multiplayer|strategy|featured/i.test(t)) ??
    (i === 0 ? "Top played" : i === 1 ? "New" : i === 2 ? "Multiplayer" : "Strategy");
  return {
    slug: g.slug,
    title: g.name,
    desc: g.tagline || g.description,
    grades: formatGrades(g.grades),
    chip,
    art: artKindForSlug(g.slug, i),
    thumbnail: g.thumbnail_url,
  };
}

function formatGrades(grades: number[]): string {
  if (!grades || grades.length === 0) return "ALL GRADES";
  const min = Math.min(...grades);
  const max = Math.max(...grades);
  return min === max ? `GRADE ${min}` : `GRADES ${min}–${max}`;
}

// Loading/error-state seed for the hero ring + gallery. Mirrors the games that
// are live in every environment, pointing at their REAL bundled thumbnails —
// which are already in the build-time derivatives manifest, so they paint
// instantly via the inlined LQIP blur-up instead of a skeleton. Because each
// entry carries the real slug + thumbnail URL, when `useGames` resolves the
// live data swaps in over the same React keys (same `key={slug}`, same image
// src) with no remount, no skeleton flash, and no reshuffle. Kept in GAME_ORDER
// and with index-derived chips so it aligns slot-for-slot with `toPoster`.
const SEED_GAMES: ReadonlyArray<{
  slug: string;
  title: string;
  desc: string;
  grades: number[];
  chip: string;
}> = [
  {
    slug: "blaster-gate",
    title: "Blaster Gate",
    desc: "Blast through the wall. First to clear wins.",
    grades: [1, 2, 3, 4, 5, 6, 7, 8],
    chip: "Top played",
  },
  {
    slug: "math-crush",
    title: "Math Crush",
    desc: "Match candies, solve math — crush your way through 24 sweet levels.",
    grades: [1, 2, 3, 4, 5],
    chip: "New",
  },
  {
    slug: "math-slice-dojo",
    title: "Math Slice Dojo",
    desc: "Slice the fruit with the right answer — Fruit Ninja meets fast math.",
    grades: [1, 2, 3, 4, 5],
    chip: "Multiplayer",
  },
  {
    slug: "wizard-duel",
    title: "Mathemagicians Duel",
    desc: "Cast spells with math — out-duel your rival wizard.",
    grades: [1, 2, 3],
    chip: "Strategy",
  },
  {
    slug: "boxing",
    title: "Math Martial Arts",
    desc: "Solve fast, land combos — K.O. your rival with math.",
    grades: [1, 2, 3],
    chip: "Strategy",
  },
  {
    slug: "math-strikers",
    title: "Math Strikers",
    desc: "Solve to shoot. Bury the most goals before the whistle.",
    grades: [1, 2, 3],
    chip: "Strategy",
  },
];

const SEED_POSTERS: Poster[] = SEED_GAMES.map((g, i) => ({
  slug: g.slug,
  title: g.title,
  desc: g.desc,
  grades: formatGrades(g.grades),
  chip: g.chip,
  art: artKindForSlug(g.slug, i),
  thumbnail: `/game-assets/${g.slug}/thumbnail.png`,
}));

const UPCOMING_POSTERS: Poster[] = [
  {
    slug: "soccer-showdown",
    title: "Soccer Showdown",
    desc: "Score goals by solving equations before the keeper saves.",
    grades: "GRADES 1–5",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/soccer-showdown.png",
  },
  {
    slug: "football-blitz",
    title: "Football Blitz",
    desc: "Run the play, answer fast, and blitz your way to a touchdown.",
    grades: "GRADES 2–6",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/football-blitz.png",
  },
  {
    slug: "hockey-faceoff",
    title: "Hockey Faceoff",
    desc: "Win the faceoff with the fastest answer on the ice.",
    grades: "GRADES 1–5",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/hockey-faceoff.png",
  },
  {
    slug: "basketball-jam",
    title: "Basketball Jam",
    desc: "Slam dunk with the right answer — fast breaks and math.",
    grades: "GRADES 2–6",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/basketball-jam.png",
  },
  {
    slug: "fighter-knockout",
    title: "Fighter Knockout",
    desc: "Land math combos to knock out your opponent round by round.",
    grades: "GRADES 3–7",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/fighter-knockout.png",
  },
  {
    slug: "golf-masters",
    title: "Golf Masters",
    desc: "Calculate the perfect shot — par, birdie, or eagle.",
    grades: "GRADES 2–5",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/golf-masters.png",
  },
  {
    slug: "snowboard-shred",
    title: "Snowboard Shred",
    desc: "Shred the slopes — land tricks by solving problems mid-air.",
    grades: "GRADES 1–5",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/snowboard-shred.png",
  },
  {
    slug: "street-soccer",
    title: "Street Soccer",
    desc: "Freestyle on the streets — nutmeg defenders with math.",
    grades: "GRADES 1–4",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/street-soccer.png",
  },
  {
    slug: "street-hoops",
    title: "Street Hoops",
    desc: "Crossovers and buckets — playground math at its finest.",
    grades: "GRADES 2–6",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/street-hoops.png",
  },
  {
    slug: "street-football",
    title: "Street Football",
    desc: "Backyard rules — hike, solve, score in the neighborhood.",
    grades: "GRADES 2–5",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/street-football.png",
  },
  {
    slug: "boxing-knockout",
    title: "Boxing Knockout",
    desc: "Jab, cross, uppercut — answer right to land the punch.",
    grades: "GRADES 3–7",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/boxing-knockout.png",
  },
  {
    slug: "cricket-smash",
    title: "Cricket Smash",
    desc: "Smash boundaries by solving problems before the bowler delivers.",
    grades: "GRADES 2–6",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/cricket-smash.png",
  },
  {
    slug: "rugby-tackle",
    title: "Rugby Tackle",
    desc: "Charge the try line — tackle problems to advance the ball.",
    grades: "GRADES 3–7",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/rugby-tackle.png",
  },
  {
    slug: "racing-speedway",
    title: "Racing Speedway",
    desc: "Floor it around the track — faster answers mean faster laps.",
    grades: "GRADES 1–5",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/racing-speedway.png",
  },
  {
    slug: "tennis-rally",
    title: "Tennis Rally",
    desc: "Rally back and forth — keep the volley alive with math.",
    grades: "GRADES 2–6",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/tennis-rally.png",
  },
  {
    slug: "bmx-tricks",
    title: "BMX Tricks",
    desc: "Pull off sick tricks at the skatepark — math unlocks combos.",
    grades: "GRADES 1–5",
    chip: "Coming soon",
    art: "quest",
    thumbnail: "/game-covers/bmx-tricks.png",
  },
];

const CLOSER_STEPS = [
  {
    n: "01",
    name: "Take the quiz",
    desc: "Twelve adaptive questions. Plays like a game. Five minutes flat.",
  },
  {
    n: "02",
    name: "Get your rank",
    desc: "Rookie to MVP — we pinpoint exactly where your kid stands.",
  },
  {
    n: "03",
    name: "Play the right game",
    desc: "Three titles picked for the gaps we just found. Begin today.",
  },
];

const WHY_ITEMS = [
  {
    title: "Learn math through video games",
    desc: "Motivation is 90% of the problem. If you can motivate a kid to do their work, that solves 99% of it. We search the web for every game kids love to play and build a safe, kid-friendly clone that\u2019s entirely focused on solving math problems. Kids find this 10\u00D7 more fun than boring worksheets \u2014 and they actually get their practice in.",
    stats: [{ value: "25", label: "min / day" }],
  },
  {
    title: "Your child progresses at their own pace",
    desc: "Your child starts by taking a diagnostic test that finds all their strengths and weaknesses in math. Then we build a personalized curriculum that targets their gaps and fills them. After that, we push them toward higher-level subjects so they\u2019re always growing.",
    stats: [{ value: "K–12", label: "100% Adaptive" }],
  },
  {
    title: "Real results parents can see",
    desc: "Every session is tracked. Parents and teachers get weekly progress reports showing accuracy, topics mastered, and time spent. We\u2019ve built our curriculum on Common Core, the SAT, the ACT, and dozens of standardized tests across every state \u2014 so your child gets the curriculum they need to excel on any test.",
    stats: [{ value: "SAT · ACT", label: "50 state standardized tests" }],
  },
];

const QUOTES = [
  "My 3rd grader asks to do math now.",
  'He stopped saying he "hates math."',
  "Finally — screen time I feel good about.",
  "Curriculum-aligned and actually fun.",
  "20 minutes a day. Real progress.",
  "Replaced a tutor. No joke.",
  "She begs to play before school.",
  "Watched him beat his fluency record three times tonight.",
];

// ─── icons (page-local, small SVGs used inline in copy + buttons) ───────────
function ArrowIcon({ size = 16 }: { size?: number }): React.ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="5" y1="12" x2="19" y2="12" />
      <polyline points="12 5 19 12 12 19" />
    </svg>
  );
}

function PlayIcon({ size = 14 }: { size?: number }): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <polygon points="6 4 20 12 6 20" />
    </svg>
  );
}

function ClipboardIcon({ size = 28 }: { size?: number }): React.ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <path d="M9 14l2 2 4-4" />
    </svg>
  );
}

function RouteIcon({ size = 28 }: { size?: number }): React.ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="6" cy="19" r="3" />
      <circle cx="18" cy="5" r="3" />
      <path d="M12 19h4.5a3.5 3.5 0 0 0 0-7h-9a3.5 3.5 0 0 1 0-7H12" />
    </svg>
  );
}

function ChartIcon({ size = 28 }: { size?: number }): React.ReactElement {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 20h18" />
      <path d="M6 16V10" />
      <path d="M11 16V6" />
      <path d="M16 16v-4" />
      <path d="M21 16V4" />
    </svg>
  );
}
